// src/pages/SuperAdminDashboard.tsx
// Enhanced Admin page that detects SuperAdmin and provides menu choice

import React, { useState, useEffect } from 'react';
import { Building2, Settings, Users, BarChart3, Shield } from 'lucide-react';
import TenantManagement from './TenantManagement';
import TenantSettings from './TenantSettings';

interface SuperAdminDashboardProps {
  userRole: string;
  tenantId: string;
  userName: string;
}

type AdminView = 'menu' | 'tenant-management' | 'tenant-settings';

const SuperAdminDashboard: React.FC<SuperAdminDashboardProps> = ({
  userRole,
  tenantId,
  userName
}) => {
  const [currentView, setCurrentView] = useState<AdminView>('menu');

  // Determine if user is SuperAdmin from SYSTEM tenant
  const isSuperAdmin = userRole === 'SuperAdmin' && tenantId === 'SYSTEM';
  const isOrgAdmin = userRole === 'OrgAdmin' || (userRole === 'SuperAdmin' && tenantId !== 'SYSTEM');

  // If not SuperAdmin, redirect directly to tenant settings
  useEffect(() => {
    if (!isSuperAdmin) {
      setCurrentView('tenant-settings');
    }
  }, [isSuperAdmin]);

  const handleBackToMenu = () => {
    setCurrentView('menu');
  };

  // Show tenant management page
  if (currentView === 'tenant-management') {
    return (
      <div className="space-y-6">
        {/* Back Navigation */}
        <div className="flex items-center">
          <button
            onClick={handleBackToMenu}
            className="flex items-center text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to Admin Menu
          </button>
        </div>
        
        <TenantManagement />
      </div>
    );
  }

  // Show tenant settings page
  if (currentView === 'tenant-settings') {
    return (
      <div className="space-y-6">
        {/* Back Navigation - only show for SuperAdmin */}
        {isSuperAdmin && (
          <div className="flex items-center">
            <button
              onClick={handleBackToMenu}
              className="flex items-center text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Back to Admin Menu
            </button>
          </div>
        )}
        
        <TenantSettings />
      </div>
    );
  }

  // Show admin menu (SuperAdmin only)
  if (isSuperAdmin) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Shield className="w-7 h-7 mr-3 text-blue-600" />
            System Administration
          </h1>
          <p className="text-gray-600 mt-1">Welcome, {userName}. Choose an administration area:</p>
        </div>

        {/* Admin Menu Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Tenant Management */}
          <div 
            onClick={() => setCurrentView('tenant-management')}
            className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
          >
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Tenant Management</h3>
                <p className="text-sm text-gray-500">Manage police departments and organizations</p>
              </div>
            </div>
            
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                Create and manage police departments
              </div>
              <div className="flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                View tenant statistics and status
              </div>
              <div className="flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                Configure subscription tiers and limits
              </div>
            </div>

            <div className="mt-4 flex items-center text-blue-600 text-sm font-medium">
              Access Tenant Management →
            </div>
          </div>

          {/* Tenant Settings */}
          <div 
            onClick={() => setCurrentView('tenant-settings')}
            className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
          >
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Settings className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">System Configuration</h3>
                <p className="text-sm text-gray-500">Configure dropdown options and settings</p>
              </div>
            </div>
            
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                Manage ranks, assignments, and categories
              </div>
              <div className="flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                Configure system defaults
              </div>
              <div className="flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                Role templates and onboarding settings
              </div>
            </div>

            <div className="mt-4 flex items-center text-green-600 text-sm font-medium">
              Access System Configuration →
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <BarChart3 className="w-5 h-5 mr-2 text-purple-600" />
            System Overview
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">-</div>
              <div className="text-sm text-gray-500">Total Tenants</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">-</div>
              <div className="text-sm text-gray-500">Active Tenants</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">-</div>
              <div className="text-sm text-gray-500">Trial Tenants</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">-</div>
              <div className="text-sm text-gray-500">Total Users</div>
            </div>
          </div>
          
          <p className="text-xs text-gray-400 mt-4 text-center">
            Statistics will load when you access Tenant Management
          </p>
        </div>
      </div>
    );
  }

  // Fallback - should not reach here
  return (
    <div className="text-center py-12">
      <h2 className="text-xl font-medium text-gray-900">Access Denied</h2>
      <p className="text-gray-600 mt-2">You don't have permission to access this area.</p>
    </div>
  );
};

export default SuperAdminDashboard;
